import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { SidebarEntry } from '@/constants/navigation'
import { useVisibleNavigation } from '@/hooks/useVisibleNavigation'
import { usePageChrome } from '@/app/providers/PageChromeProvider'
import { resolvePageTitleFromPath } from '@/utils/page-title'
import {
  buildNavCrumbs,
  buildNavSections,
  findActiveNav,
  flattenNavPages,
  type ActiveNav,
} from './nav-model'

/** Permission-filtered nav + derived sections / active leaf / palette pages. */
export function useNavModel() {
  const entries = useVisibleNavigation()
  const { pathname } = useLocation()
  const sections = useMemo(() => buildNavSections(entries), [entries])
  const pages = useMemo(() => flattenNavPages(entries), [entries])
  const activeId = findActiveNav(entries, pathname)
  // Stable object identity while the active leaf is unchanged.
  const active = useMemo<ActiveNav>(
    () => ({ id: activeId.id, groupId: activeId.groupId, subId: activeId.subId }),
    [activeId.id, activeId.groupId, activeId.subId],
  )
  return { entries, sections, pages, active }
}

export function useNavCrumbs(entries: SidebarEntry[], active: ActiveNav) {
  const { pathname, search } = useLocation()
  const { chrome } = usePageChrome()
  const title = resolvePageTitleFromPath(pathname, search) ?? chrome.title ?? ''
  return useMemo(
    () => buildNavCrumbs(entries, active, pathname, title),
    [entries, active, pathname, title],
  )
}

/* ───────── collapsed-rail tooltip / flyout ───────── */
export type RailFloatTarget = { entry: SidebarEntry; top: number }

export interface RailFloatApi {
  target: RailFloatTarget | null
  visible: boolean
  open: (entry: SidebarEntry, el: HTMLElement) => void
  close: () => void
  keep: () => void
  hide: () => void
}

/** Hover/focus intent for the icon rail. Only visible while `enabled` (collapsed). */
export function useRailFloat(enabled: boolean): RailFloatApi {
  const [target, setTarget] = useState<RailFloatTarget | null>(null)
  const [shown, setShown] = useState(false)
  const shownRef = useRef(false)
  const timer = useRef<number | undefined>(undefined)

  const setShownBoth = useCallback((next: boolean) => {
    shownRef.current = next
    setShown(next)
  }, [])

  const open = useCallback(
    (entry: SidebarEntry, el: HTMLElement) => {
      if (!enabled) return
      window.clearTimeout(timer.current)
      const rect = el.getBoundingClientRect()
      const go = () => {
        setTarget({ entry, top: entry.type === 'group' ? rect.top - 6 : rect.top + rect.height / 2 })
        setShownBoth(true)
      }
      // Instant while moving between icons; short intent delay otherwise.
      if (shownRef.current) go()
      else timer.current = window.setTimeout(go, 90)
    },
    [enabled, setShownBoth],
  )
  const close = useCallback(() => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setShownBoth(false), 140)
  }, [setShownBoth])
  const keep = useCallback(() => window.clearTimeout(timer.current), [])
  const hide = useCallback(() => {
    window.clearTimeout(timer.current)
    setShownBoth(false)
  }, [setShownBoth])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  // Derived (no effect): expanding the sidebar hides any float immediately.
  return { target, visible: enabled && shown, open, close, keep, hide }
}

/* ───────── Ctrl+K page palette ───────── */
export interface PaletteApi {
  open: boolean
  /** Bumps on every open → palette body remounts with a fresh query. */
  session: number
  show: () => void
  hide: () => void
}

export function useCommandPalette(): PaletteApi {
  const [state, setState] = useState({ open: false, session: 0 })
  const show = useCallback(() => setState((s) => (s.open ? s : { open: true, session: s.session + 1 })), [])
  const hide = useCallback(() => setState((s) => (s.open ? { ...s, open: false } : s)), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setState((s) => (s.open ? { ...s, open: false } : { open: true, session: s.session + 1 }))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return { open: state.open, session: state.session, show, hide }
}
